import {
  Box,
  Divider,
  FormLabel,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { formatUSPhoneNumber } from '@product-framework/components/FormFields/PhoneNumber';
import { IconLibrary } from '@product-framework/index';
import { useLocalization, useMasterData } from '@ui-framework/index';
import PermissionButton from '@product-framework/components/PermissionButton';
import DialogCloseButton from '@product-framework/components/Dialogs/DialogCloseButton';
import InfoBlock from './InfoBlock';
import FormLabelCustom from './FormLabel';
import DistanceFromCustomer from './DistanceFromCustomer';

function ContactInfoRow({ Icon, label, children }) {
  return (
    <Stack direction="row" alignItems="flex-start" spacing={0.75}>
      <Icon fontSize="small" color="action" sx={{ mt: label ? 0.35 : 0 }} />
      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        {label ? (
          <Typography variant="caption" color="text.secondary" component="div">
            {label}
          </Typography>
        ) : null}
        <FormLabelCustom>{children}</FormLabelCustom>
      </Stack>
    </Stack>
  );
}

function NotAvailable() {
  return (
    <Typography variant="body2" color="text.secondary">
      <FormattedMessage id="NOT_AVAILABLE" defaultMessage="Not Available" />
    </Typography>
  );
}

function ServiceProviderInfo({
  serviceProvider,
  currentJob,
  setDialog,
  onClose,
}) {
  const theme = useTheme();
  const { sp, spAddress } = serviceProvider;
  const { formatMessage } = useLocalization();

  const spContact = sp?.contacts?.find(
    c => c.locationId === spAddress.locationId,
  );
  const notificationEmail = spContact?.notificationEmail;

  const { pendingServiceJobStatus } = useMasterData();
  const assign = pendingServiceJobStatus.includes(currentJob?.serviceJobStatus);
  const activeServiceProvider = sp?.status === 'ACTIVE';

  const jobAssignedLocationId = currentJob?.serviceProviderLocation?.id;
  const differentDispatchLocation =
    jobAssignedLocationId != null &&
    spAddress?.locationId != null &&
    String(jobAssignedLocationId) !== String(spAddress.locationId);
  // Same SP only blocks when we know it is the same dispatch location; missing ids keep legacy disable.
  const assignToThisSpIsNoOp =
    sp?.id === currentJob?.serviceProviderId && !differentDispatchLocation;

  const isUnassigned = !currentJob?.serviceProviderId;
  const showReassign =
    !isUnassigned &&
    (sp?.id !== currentJob?.serviceProviderId || differentDispatchLocation);

  return (
    <Box
      sx={{
        position: 'relative',
        minWidth: '300px',
        padding: '5px 10px 10px 10px',
      }}>
      {onClose ? <DialogCloseButton onClose={onClose} /> : null}
      <Grid2 sx={{ marginBottom: '0px' }}>
        <FormLabel sx={{ mb: 0, p: 0, color: theme.palette.primary.main }}>
          <FormattedMessage
            id="SERVICE_PROVIDER"
            defaultMessage="Service Provider"
          />
        </FormLabel>
        <Typography sx={{ fontSize: '18px', fontWeight: 500, mt: -0.2, mb: 1 }}>
          {sp.name}
        </Typography>
        <Typography sx={{ fontSize: '12px', fontWeight: 500, mt: -0.9, mb: 1 }}>
          <FormLabel component="span">
            <FormattedMessage
              id="DISPATCH_LOCATION_NAME"
              defaultMessage="Location Name"
            />
            : {spContact?.locationName}
          </FormLabel>
        </Typography>
      </Grid2>
      <Grid2>
        <InfoBlock
          label={formatMessage({
            id: 'LEGEND_SERVICES_BRAND',
            defaultMessage: 'Services Brand',
          })}
          value={spContact?.servicesBrand ? 'Yes' : 'No'}
        />
        <InfoBlock
          label={formatMessage({
            id: 'LEGEND_SERVICES_INDUSTRY',
            defaultMessage: 'Services Industry',
          })}
          value={spContact?.servicesIndustry ? 'Yes' : 'No'}
        />
        <InfoBlock
          label={formatMessage({
            id: 'LEGEND_PREVIOUSLY_REJECTED',
            defaultMessage: 'Previously Rejected',
          })}
          value={sp?.dispatchEvent?.status === 'REJECTED' ? 'Yes' : 'No'}
        />

        <InfoBlock
          label={formatMessage({
            id: 'LEGEND_SERVICES_PRODUCT_LINE',
            defaultMessage: 'Services Product Line',
          })}
          value={spContact?.servicesProductLine ? 'Yes' : 'No'}
        />
      </Grid2>
      <Divider sx={{ marginTop: '5px', marginBottom: '5px' }} />
      <Grid2 sx={{ marginBottom: '10px' }}>
        <DistanceFromCustomer
          jobLocation={{
            lat: currentJob?.locationLat,
            lng: currentJob?.locationLng,
          }}
          serviceProviderAddressLocation={{
            lat: spAddress?.locationLat,
            lng: spAddress?.locationLng,
          }}
        />
      </Grid2>
      <Divider sx={{ marginTop: '5px', marginBottom: '5px' }} />
      <Grid2 sx={{ marginBottom: '5px' }}>
        <FormLabel>
          <FormattedMessage id="CONTACT_INFO" defaultMessage="Contact Info" />
        </FormLabel>
      </Grid2>
      <Grid2>
        <Stack spacing={1}>
          <ContactInfoRow
            Icon={IconLibrary.Email}
            label={formatMessage({
              id: 'PRIMARY_CONTACT',
              defaultMessage: 'Primary Contact',
            })}>
            {spContact?.user?.email || <NotAvailable />}
          </ContactInfoRow>
          <ContactInfoRow
            Icon={IconLibrary.Email}
            label={formatMessage({
              id: 'DISPATCHNOTIFICATIONS_FIELD_LABEL',
              defaultMessage: 'Dispatch Notification Email',
            })}>
            {notificationEmail || <NotAvailable />}
          </ContactInfoRow>
          <ContactInfoRow Icon={IconLibrary.Phone}>
            {spContact?.user?.phoneNumber ? (
              formatUSPhoneNumber(spContact?.user?.phoneNumber)
            ) : (
              <NotAvailable />
            )}
          </ContactInfoRow>
        </Stack>
      </Grid2>
      <Divider sx={{ marginTop: '5px', marginBottom: '5px' }} />
      <Grid2
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: 1 }}>
        {activeServiceProvider ? (
          <PermissionButton
            permissionName="CAN_UPDATE_JOB_FROM_MAP"
            sx={{ textTransform: 'none' }}
            variant="outlined"
            size="small"
            onClick={() => setDialog('MESSAGE')}>
            <FormattedMessage id="SEND_MESSAGE" defaultMessage="Send Message" />
          </PermissionButton>
        ) : (
          <>&nbsp;</>
        )}
        <PermissionButton
          permissionName="CAN_UPDATE_JOB_FROM_MAP"
          size="small"
          variant="contained"
          disabled={assignToThisSpIsNoOp || !assign}
          onClick={() => setDialog('ASSIGN')}>
          {showReassign ? (
            <FormattedMessage id="BUTTON_REASSIGN" defaultMessage="Reassign" />
          ) : (
            <FormattedMessage id="BUTTON_ASSIGN" defaultMessage="Assign" />
          )}
        </PermissionButton>
      </Grid2>
    </Box>
  );
}

export default ServiceProviderInfo;
